








import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import { FILEENDPOINT } from "@/app.config";
import { BehaviorEnum } from "@/services/posts/types";
import { calculateMediaContentDimensions } from "@/services/render/renderService";
import { logError } from "@/services/logging/loggingService";

@Component
export default class VideoSubRender extends Vue {
        @Prop() postId!: string;
        @Prop() videoPath!: string;
        @Prop() behavior!: string;
        @Prop() videoStyle!: string;
        @Prop() playWithSound!: boolean;
        @Prop() private containerHeight!: number;
        @Prop() private containerWidth!: number;
        @Prop() videoActive!: number;
        @Prop() lastModifiedDate!: Date;

        staticVideo = {} as HTMLVideoElement;
        videoPlaying = false;
        loaded = false;
        checkPlaybackInterval = 0 as number | null;
        lastKnownTime = 0 as number;
        lastTimeUpdate = new Date() as Date;

        @Watch("videoActive", { immediate: true, deep: true })
        onActiveChange(value: number) {
            if (value === 0 || !this.staticVideo || isNaN(this.staticVideo.readyState)) {
                if (this.videoActive === 0 && this.loaded) {
                    this.videoPlaying = false;
                    this.staticVideo.currentTime = 0;
                    this.staticVideo.pause();
                }
                return;
            }

            if (this.staticVideo.readyState === 0) {
                // Video hasn't loaded any data yet - emit error to remove from rotation
                this.$emit("setNextItem", { error: true, duration: 0 });
                return;
            }

            this.videoPlaying = false;
            this.staticVideo.currentTime = 0;
            this.playVideo();
        }

        // lifecycle hooks
        mounted(): void {
            this.staticVideo = this.$refs.video as HTMLVideoElement;
            this.loadVideo();
        }

        beforeDestroy() {
            this.clearPlaybackInterval();
        }

        // computed
        get videoUrl(): string {
            return `${FILEENDPOINT}${this.videoPath}/${this.containerHeight}/${this.containerWidth}?date=${this.lastModifiedDate}`;
        }

        // methods
        startPlaybackCheck() {
            this.clearPlaybackInterval();
            this.checkPlaybackInterval = setInterval(() => {
                if (!this.staticVideo) return;

                const currentTime = this.staticVideo.currentTime;
                if (currentTime !== this.lastKnownTime) {
                    this.lastKnownTime = currentTime;
                    this.lastTimeUpdate = new Date();
                } else {
                    const now = new Date();
                    const timePassed = (now.getTime() - this.lastTimeUpdate.getTime()) / 1000;
                    if (timePassed > 5) {
                        logError(`Playback stalled: Video has stopped or has not advanced for over 5 seconds. ` +
                            `Current time: ${this.staticVideo.currentTime}s, Last known time: ${this.lastKnownTime}s. ` +
                            `Post ID: ${this.postId}, Video Path: ${this.videoPath}`);
                        this.clearPlaybackInterval();
                        // Skip to next item but do not set error flag to retry playing the video.
                        this.$emit("setNextItem", { duration: currentTime });
                    }
                }
            }, 1000); // Check every second
        }

        clearPlaybackInterval() {
            if (this.checkPlaybackInterval) {
                clearInterval(this.checkPlaybackInterval);
            }
            this.checkPlaybackInterval = null;
            this.lastKnownTime = 0;
            this.lastTimeUpdate = new Date();
        }

        loadVideo() {
            this.staticVideo.muted = !this.playWithSound;
            this.staticVideo.autoplay = false;

            // video events
            this.staticVideo.onplaying = () => {
                this.videoPlaying = true;
                this.$emit("triggerDurationIndicator", this.staticVideo.duration);
                this.$emit("triggerPostCountTimer", this.staticVideo.duration);
            };

            this.staticVideo.onended = () => {
                this.clearPlaybackInterval();
                this.videoPlaying = false;
                this.$emit("setNextItem", { duration: this.staticVideo.duration });
            };

            this.staticVideo.onerror = () => {
                this.clearPlaybackInterval();
                this.$emit("setNextItem", { error: true, duration: 0 });
            };

            // Reset some styles that might ruin the preview and let the css take over
            this.staticVideo.style.top = "";
            this.staticVideo.style.left = "";
            this.staticVideo.style.right = "";
            this.staticVideo.style.bottom = "";
            this.staticVideo.style.position = "";
            this.staticVideo.style.margin = "";

            switch (this.behavior) {
            case BehaviorEnum.letterBox:
                this.setLetterBoxBehavior();
                break;
            case BehaviorEnum.crop:
                this.staticVideo.onloadeddata = () => {
                    var ratio = Math.max(this.containerWidth / this.staticVideo.videoWidth, this.containerHeight / this.staticVideo.videoHeight);
                    if (ratio < 1) {
                        this.staticVideo.style.transform = `scale(${ratio}) translate(-50%, -50%)`;
                    }
                };
                this.staticVideo.src = `${FILEENDPOINT}${this.videoPath}`;
                break;
            default:
                this.staticVideo.src = this.videoUrl;
                break;
            }

            if (this.videoActive && !this.videoPlaying) {
                this.playVideo();
            }

            this.loaded = true;
        }

        playVideo() {
            if (this.videoPlaying) return;
            this.videoPlaying = true;
            this.staticVideo.pause();
            this.staticVideo.autoplay = true;
            this.staticVideo.play();
            this.startPlaybackCheck();
        }

        setLetterBoxBehavior() {
            this.staticVideo.onloadeddata = () => {
                const dimensions = calculateMediaContentDimensions(
                    this.staticVideo.videoWidth,
                    this.staticVideo.videoHeight,
                    this.containerWidth,
                    this.containerHeight
                );
                this.staticVideo.width = dimensions.width;
                this.staticVideo.style.width = dimensions.width + "px";
                this.staticVideo.height = dimensions.height;
                this.staticVideo.style.height = dimensions.height + "px";
            };
            this.staticVideo.src = this.videoUrl;
        }
}
